/**
 * Capacitación del staff — esquemas Zod del catálogo y de la API `/api/training`
 * (`docs/spec/capacitacion.md`, contrato en `docs/api/contracts.md` §5.3).
 *
 * El catálogo (módulos, lecciones, preguntas) es DATA versionada que vive en este paquete,
 * no en Mongo: el motor se reusa en otro cliente cambiando sólo el contenido.
 */
import { z } from "zod";
import { USER_ROLES } from "../enums.js";
import { objectId } from "../schemas/base.js";
// ── Catálogo ─────────────────────────────────────────────────────────────────
/** Clave de lección: `<módulo>.<tema>` en minúsculas y guiones (`mostrador.cobrar`). */
export const trainingLessonKey = z
    .string()
    .regex(/^[a-z]+\.[a-z0-9-]+$/, "Clave de lección inválida");
export const trainingModuleKey = z.enum([
    "inicio",
    "mostrador",
    "socios",
    "clases",
    "kiosko",
    "personal",
    "caja",
    "facturacion",
    "gastos",
    "comunicacion",
    "reportes",
    "configuracion",
    "profes",
]);
export const trainingModule = z.object({
    key: trainingModuleKey,
    title: z.string().min(1),
    order: z.number().int().min(1),
});
export const trainingLesson = z.object({
    key: trainingLessonKey,
    module: trainingModuleKey,
    title: z.string().min(1),
    /** Roles del staff que la cursan. Socios y familias no tienen curso. */
    roles: z.array(z.enum(USER_ROLES)).min(1),
    /** Pantalla del portal que enseña (`web/src/app/screens.ts`); `null` = conceptual. */
    screenId: z.string().min(1).nullable(),
    /** Tiene práctica simulada (el motor de simulación lo construye otra tarjeta). */
    hasPractice: z.boolean(),
    /** 2 a 4 oraciones: qué aprende. */
    summary: z.string().min(1),
    /** 3 a 6 puntos: el resumen consultable (pasos y reglas que el sistema no deja saltear). */
    keyPoints: z.array(z.string().min(1)).min(3).max(6),
    estimatedMin: z.number().int().min(1),
    /** Sube cuando cambia el contenido: el quiz vuelve a pedirse (RN-CAP-07). */
    contentVersion: z.number().int().min(1),
});
export const trainingOptionId = z.enum(["a", "b", "c", "d"]);
export const trainingQuestion = z
    .object({
    /** Estable: `<lessonKey>#<n>`. Es lo que guardan los intentos. */
    id: z.string().regex(/^[a-z]+\.[a-z0-9-]+#\d+$/, "Id de pregunta inválido"),
    lessonKey: trainingLessonKey,
    prompt: z.string().min(1),
    options: z
        .array(z.object({ id: trainingOptionId, text: z.string().min(1) }))
        .min(3)
        .max(4),
    correctOptionId: trainingOptionId,
    /** Por qué es esa: se muestra al corregir. */
    explanation: z.string().min(1),
})
    .refine((q) => q.options.some((o) => o.id === q.correctOptionId), {
    message: "La opción correcta no está entre las opciones",
})
    .refine((q) => new Set(q.options.map((o) => o.id)).size === q.options.length, {
    message: "Ids de opción repetidos",
});
/** Pregunta tal como sale a quien rinde: SIN la respuesta ni la explicación. */
export const trainingQuestionPublic = z.object({
    id: z.string(),
    lessonKey: trainingLessonKey,
    prompt: z.string(),
    options: z.array(z.object({ id: trainingOptionId, text: z.string() })),
});
// ── Manifiesto de videos (lo publica el hub de Deepyze) ──────────────────────
/** Contrato EXACTO del manifiesto (RN-CAP-10). Otra `version` = manifiesto inválido. */
export const trainingVideoManifest = z.object({
    version: z.literal(1),
    generatedAt: z.string().datetime({ offset: true }),
    lessons: z.record(z.string(), z.object({
        url: z.string().url(),
        posterUrl: z.string().url().nullable(),
        durationSec: z.number().int().min(0),
        videoVersion: z.number().int().min(1),
        expiresAt: z.string().datetime({ offset: true }),
    })),
});
export const TRAINING_VIDEO_STATUSES = [
    "ready",
    "not_produced",
    "not_configured",
    "unavailable",
];
/**
 * Estado del video de una lección. `unavailable` = no se pudo ver el manifiesto (caído,
 * inválido o la URL firmada venció): es «no sé», NUNCA «no hay video».
 */
export const trainingVideoState = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("ready"),
        url: z.string(),
        posterUrl: z.string().nullable(),
        durationSec: z.number().int(),
        videoVersion: z.number().int(),
        expiresAt: z.string(),
    }),
    z.object({ status: z.literal("not_produced") }),
    z.object({ status: z.literal("not_configured") }),
    z.object({ status: z.literal("unavailable") }),
]);
export const trainingVideosResponse = z.object({
    /** Estado del manifiesto en sí. */
    manifest: z.enum(["not_configured", "ok", "unavailable"]),
    lessons: z.record(z.string(), trainingVideoState),
});
// ── Progreso ─────────────────────────────────────────────────────────────────
/**
 * - `not_started`: sin nada registrado.
 * - `in_progress`: algo hecho, falta algo.
 * - `completed`: video (si hay), práctica (si tiene) y quiz aprobado de la versión vigente.
 * - `updated`: la lección cambió de versión después de aprobarla; el quiz vuelve a pedirse.
 */
export const trainingLessonStatus = z.enum(["not_started", "in_progress", "completed", "updated"]);
export const trainingLessonProgress = z.object({
    status: trainingLessonStatus,
    contentVersion: z.number().int().nullable(),
    videoWatchedPct: z.number().min(0).max(100),
    videoCompletedAt: z.string().nullable(),
    practiceCompletedAt: z.string().nullable(),
    practiceErrors: z.number().int().nullable(),
    quizPassedAt: z.string().nullable(),
    bestScore: z.number().int().nullable(),
    completedAt: z.string().nullable(),
    /** Qué falta para completarla (vacío si está completa). */
    missing: z.array(z.enum(["video", "practice", "quiz"])),
});
const lessonSummaryItem = trainingLesson.extend({ progress: trainingLessonProgress });
export const trainingCertificateOut = z.object({
    id: z.string(),
    role: z.enum(USER_ROLES),
    issuedAt: z.string(),
    finalScore: z.number().int(),
    catalogVersion: z.string(),
    /** El curso del rol cambió desde que se emitió. */
    outdated: z.boolean(),
});
export const trainingMeResponse = z.object({
    role: z.enum(USER_ROLES),
    modules: z.array(trainingModule.extend({ lessons: z.array(lessonSummaryItem) })),
    lessonsTotal: z.number().int(),
    lessonsCompleted: z.number().int(),
    /** % de lecciones completas, entero 0-100. */
    progressPct: z.number().int().min(0).max(100),
    finalExam: z.object({
        available: z.boolean(),
        questionCount: z.number().int(),
        passScore: z.number().int(),
    }),
    certificate: trainingCertificateOut.nullable(),
    /** Plazo informativo (no bloquea nada): alta del usuario + `capacitacion.plazoDias`. */
    dueAt: z.string(),
    overdue: z.boolean(),
    videos: z.enum(["not_configured", "ok", "unavailable"]),
});
export const trainingLessonDetailResponse = z.object({
    lesson: trainingLesson,
    module: trainingModule,
    progress: trainingLessonProgress,
    video: trainingVideoState,
    questionCount: z.number().int(),
    passScore: z.number().int(),
});
// ── Requests ─────────────────────────────────────────────────────────────────
/**
 * Query de las rutas de `/api/training` que no admiten parámetros de query (todas salvo las
 * del seguimiento del equipo): también pasan por Zod, y lo que llegue se descarta.
 */
export const trainingNoQuery = z.object({});
export const trainingLessonParams = z.object({ key: trainingLessonKey });
export const trainingVideoProgressBody = z
    .object({ watchedPct: z.number().min(0).max(100) })
    .strict();
export const trainingPracticeBody = z
    .object({
    errors: z.number().int().min(0).max(10_000),
    durationSec: z
        .number()
        .int()
        .min(0)
        .max(24 * 60 * 60),
})
    .strict();
/** Cuestionario servido: las preguntas barajadas y el token que ata la entrega a ellas. */
export const trainingQuizResponse = z.object({
    kind: z.enum(["quiz", "final"]),
    lessonKey: trainingLessonKey.nullable(),
    /** Firmado por el server: qué preguntas se sirvieron, a quién y cuándo. */
    attemptToken: z.string(),
    passScore: z.number().int(),
    questions: z.array(trainingQuestionPublic),
});
export const trainingQuizSubmitBody = z
    .object({
    attemptToken: z.string().min(1).max(8_000),
    answers: z
        .array(z.object({ questionId: z.string().min(1).max(80), optionId: trainingOptionId }))
        .min(1)
        .max(200),
})
    .strict();
export const trainingQuizResult = z.object({
    attemptId: z.string(),
    score: z.number().int().min(0).max(100),
    passed: z.boolean(),
    passScore: z.number().int(),
    results: z.array(z.object({
        questionId: z.string(),
        prompt: z.string(),
        selectedOptionId: trainingOptionId,
        correctOptionId: trainingOptionId,
        correct: z.boolean(),
        explanation: z.string(),
    })),
    /** Sólo en el quiz de lección: cómo quedó la lección después de rendir. */
    progress: trainingLessonProgress.optional(),
    /** Sólo en el final aprobado. */
    certificate: trainingCertificateOut.nullable().optional(),
});
// ── Seguimiento del equipo ───────────────────────────────────────────────────
export const TRAINING_TEAM_STATUSES = [
    "not_started",
    "in_progress",
    "completed",
    "overdue",
];
export const trainingTeamStatus = z.enum(TRAINING_TEAM_STATUSES);
export const trainingTeamQuery = z
    .object({
    branchId: objectId.optional(),
    role: z.enum(USER_ROLES).optional(),
    status: trainingTeamStatus.optional(),
})
    .strict();
export const trainingTeamQuestionsQuery = z
    .object({
    branchId: objectId.optional(),
    role: z.enum(USER_ROLES).optional(),
    limit: z.coerce.number().int().min(1).max(332).default(50),
})
    .strict();
export const trainingTeamUserParams = z.object({ userId: objectId });
export const trainingTeamRow = z.object({
    userId: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.enum(USER_ROLES),
    branches: z.array(z.object({ id: z.string(), name: z.string() })),
    lessonsTotal: z.number().int(),
    lessonsCompleted: z.number().int(),
    progressPct: z.number().int(),
    /** Promedio de la mejor nota de cada lección rendida; `null` = no rindió ninguna. */
    avgScore: z.number().int().nullable(),
    attempts: z.number().int(),
    final: z.object({
        passed: z.boolean(),
        /** Mejor nota del final; `null` = nunca lo rindió. */
        score: z.number().int().nullable(),
        at: z.string().nullable(),
    }),
    /** `null` = nunca entró al curso. */
    lastActivityAt: z.string().nullable(),
    dueAt: z.string(),
    overdue: z.boolean(),
    status: trainingTeamStatus,
});
export const trainingTeamResponse = z.object({ items: z.array(trainingTeamRow) });
export const trainingAttemptOut = z.object({
    id: z.string(),
    kind: z.enum(["quiz", "final", "practice"]),
    lessonKey: z.string().nullable(),
    score: z.number().int().nullable(),
    passed: z.boolean(),
    submittedAt: z.string(),
    /** Preguntas que respondió mal, con el texto vigente del catálogo. */
    failed: z.array(z.object({ questionId: z.string(), prompt: z.string().nullable() })),
    /** Sólo en prácticas. */
    errors: z.number().int().nullable(),
    durationSec: z.number().int().nullable(),
});
export const trainingTeamUserResponse = z.object({
    user: trainingTeamRow,
    lessons: z.array(z.object({
        key: z.string(),
        module: trainingModuleKey,
        title: z.string(),
        progress: trainingLessonProgress,
        attempts: z.array(trainingAttemptOut),
    })),
    finals: z.array(trainingAttemptOut),
});
export const trainingTeamQuestionsResponse = z.object({
    items: z.array(z.object({
        questionId: z.string(),
        prompt: z.string(),
        lessonKey: z.string(),
        lessonTitle: z.string(),
        /** Veces que se respondió (quiz y final). */
        n: z.number().int(),
        wrong: z.number().int(),
        /** % de error, entero 0-100. */
        errorPct: z.number().int(),
    })),
});
//# sourceMappingURL=schemas.js.map